import type { ChatEnLista, Frente } from '@/api/frentes.api';

/**
 * LAS TRES VISTAS DE «FRENTES» (tanda 6), en lo que tienen de PURO.
 *
 * Kervens: «los frentes yo debería verlos también de tres maneras… dame tres
 * opciones y déjalas ahí y que el cliente escoja cómo verlo». La lista de
 * siempre, un mosaico con una tarjeta por frente y un tablero con una columna
 * por frente. Aquí vive cómo se reparten los chats y qué hay que mandar para
 * pasar uno de un frente a otro; dibujarlos es de `components/frentes`.
 */

export type VistaDeFrentes = 'lista' | 'mosaico' | 'tablero';
export const VISTAS_DE_FRENTES: readonly VistaDeFrentes[] = ['lista', 'mosaico', 'tablero'];

/** El grupo de los chats que no están en ningún frente. No es un frente: no tiene id en el servidor. */
export const SIN_CLASIFICAR = '__sin';
const COLOR_SIN_CLASIFICAR = '#94a3b8';

export interface GrupoDeChats {
  /** El id del frente, o `SIN_CLASIFICAR`. */
  id: string;
  nombre: string;
  /** Hex. El del frente; gris para «Sin clasificar». */
  color: string;
  conCandado: boolean;
  /** Por última actividad, el más reciente primero. */
  chats: ChatEnLista[];
  sinLeer: number;
  /** La del chat más reciente; `null` si no hay ninguno (o ninguno ha tenido mensajes). */
  ultimaActividad: string | null;
}

/** Un paso que se le manda al servidor: poner el chat en ese frente, o sacarlo. */
export interface PasoDeFrente {
  frenteId: string;
  poner: boolean;
}

/** Cuándo se movió el chat por última vez. */
export function actividadDe(c: ChatEnLista): string | null {
  return c.lastMessageAt ?? c.ultimo?.cuando ?? null;
}

/** La última línea del chat, como se lee en cualquier lista. */
export function ultimaLinea(c: ChatEnLista): string {
  if (!c.ultimo) return 'Sin mensajes';
  return `${c.ultimo.mio ? 'Tú: ' : ''}${c.ultimo.texto || '(archivo)'}`;
}

function grupo(molde: Pick<GrupoDeChats, 'id' | 'nombre' | 'color' | 'conCandado'>, chats: ChatEnLista[]): GrupoDeChats {
  // Sin fecha, al final: `Date.parse` de lo que no es fecha no es un número.
  const cuando = (c: ChatEnLista) => Date.parse(actividadDe(c) ?? '') || 0;
  const ordenados = [...chats].sort((x, y) => cuando(y) - cuando(x));
  return {
    ...molde,
    chats: ordenados,
    sinLeer: ordenados.reduce((n, c) => n + (c.sinLeer || 0), 0),
    ultimaActividad: ordenados[0] ? actividadDe(ordenados[0]) : null,
  };
}

/**
 * Un grupo por frente (en su orden) y al final «Sin clasificar», que sale
 * SIEMPRE: es donde se suelta un chat para sacarlo de su frente. Un chat que
 * está en dos frentes sale en los dos.
 *
 * «Sin clasificar» es lo que no cayó en ningún grupo, no «lo que no trae
 * frentes»: un chat que apunta a un frente que ya no existe tiene que poder
 * encontrarse en algún lado.
 */
export function chatsPorFrente(frentes: Frente[], chats: ChatEnLista[]): GrupoDeChats[] {
  const enOrden = [...frentes].sort((x, y) => x.orden - y.orden);
  const conocidos = new Set(enOrden.map((f) => f.id));
  const grupos = enOrden.map((f) =>
    grupo(
      { id: f.id, nombre: f.nombre, color: f.color, conCandado: f.conCandado },
      chats.filter((c) => c.frentes.some((x) => x.id === f.id)),
    ),
  );
  const sueltos = chats.filter((c) => !c.frentes.some((x) => conocidos.has(x.id)));
  return [...grupos, grupo({ id: SIN_CLASIFICAR, nombre: 'Sin clasificar', color: COLOR_SIN_CLASIFICAR, conCandado: false }, sueltos)];
}

/**
 * Qué hay que mandar al servidor para pasar un chat de un grupo a otro: ponerlo
 * en el de destino y quitarlo del de origen. Solo lo que de verdad cambia algo:
 * si ya está en el destino, solo sale del origen; al mismo grupo, nada.
 * «Sin clasificar» no es un frente: ir hacia él es solo salir del origen, y
 * venir de él es solo entrar al destino.
 *
 * Primero se pone y después se quita: si el segundo paso falla, el chat queda
 * en los dos frentes en vez de quedar en ninguno.
 */
export function movimientos(chat: ChatEnLista, de: string, a: string): PasoDeFrente[] {
  if (de === a) return [];
  const esta = (id: string) => chat.frentes.some((f) => f.id === id);
  const pasos: PasoDeFrente[] = [];
  if (a !== SIN_CLASIFICAR && !esta(a)) pasos.push({ frenteId: a, poner: true });
  if (de !== SIN_CLASIFICAR && esta(de)) pasos.push({ frenteId: de, poner: false });
  return pasos;
}

/**
 * Cómo queda el chat en pantalla apenas se mueve, sin esperar al servidor (si
 * el servidor falla, la pantalla le devuelve los frentes que tenía).
 */
export function conPasos(chat: ChatEnLista, pasos: PasoDeFrente[], frentes: Frente[]): ChatEnLista {
  let suyos = chat.frentes;
  for (const { frenteId, poner } of pasos) {
    if (!poner) suyos = suyos.filter((f) => f.id !== frenteId);
    else if (!suyos.some((f) => f.id === frenteId)) {
      const f = frentes.find((x) => x.id === frenteId);
      if (f) suyos = [...suyos, { id: f.id, nombre: f.nombre, color: f.color }];
    }
  }
  return { ...chat, frentes: suyos };
}

/**
 * El color del frente, aguado, para teñir un encabezado sin taparle la letra.
 * Los colores de los frentes son siempre `#rrggbb` (lo valida el servidor); a
 * cualquier otra cosa no se le pega nada.
 */
export function tinte(color: string, alfa = '1f'): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alfa}` : color;
}

/**
 * El candado de un chat que se abre suelto (su pestaña, o el enlace directo): el nombre del
 * frente con llave en el que está, o `null` si no lo guarda ninguno.
 *
 * Si hay frentes con llave y el chat no está entre los que se pudieron mirar (la lista trae los
 * más recientes), se pide la llave igual: un candado que se abre cuando no sabe no es un candado.
 */
export function llaveDelChat(frentes: Frente[], chats: ChatEnLista[], chatId: string): string | null {
  const conLlave = frentes.filter((f) => f.conCandado);
  if (conLlave.length === 0) return null;
  const chat = chats.find((c) => c.id === chatId);
  if (!chat) return conLlave[0]!.nombre;
  return conLlave.find((f) => chat.frentes.some((x) => x.id === f.id))?.nombre ?? null;
}
